import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Phone, MessageSquare, Mail, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function ClientManagement() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');

  const { data: clients, isLoading } = useQuery({
    queryKey: ['client-management', user?.id],
    queryFn: async () => {
      const { data: userProperties } = await supabase
        .from('properties').select('id').eq('user_id', user!.id);
      const propIds = (userProperties || []).map(p => p.id);

      let qb = supabase
        .from('leads')
        .select('*, properties(title, city), projects(name, city), lead_notes(id)')
        .eq('status', 'converted')
        .order('updated_at', { ascending: false });

      if (propIds.length > 0) {
        qb = qb.or(`agent_id.eq.${user!.id},property_id.in.(${propIds.join(',')})`);
      } else {
        qb = qb.eq('agent_id', user!.id);
      }

      const { data, error } = await qb;
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  const filtered = (clients || []).filter((c: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.phone?.includes(q) || c.email?.toLowerCase().includes(q);
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold">Client Management</h2>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" /> Client Management
        </h2>
        <p className="text-sm text-muted-foreground">Converted leads — your active clients.</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 h-9 text-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {clients?.length === 0 ? 'No converted leads yet. Move leads to "Converted" in the pipeline.' : 'No clients match your search.'}
        </div>
      ) : (
        <div className="rounded-2xl border overflow-hidden bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Property / Project</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Converted</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((client: any) => (
                <TableRow key={client.id}>
                  <TableCell>
                    <div className="font-medium text-sm">{client.name}</div>
                    {client.source && <Badge variant="secondary" className="text-[10px] mt-0.5 capitalize">{client.source}</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{client.phone}</div>
                    {client.email && <div className="text-xs text-muted-foreground">{client.email}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {client.properties && (
                      <span>{typeof client.properties.title === 'object' ? (client.properties.title as any)?.en : client.properties.title}</span>
                    )}
                    {client.projects && (
                      <span>{typeof client.projects.name === 'object' ? (client.projects.name as any)?.en : client.projects.name}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{client.lead_notes?.length || 0}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(client.updated_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                        <a href={`tel:${client.phone}`}><Phone className="h-3.5 w-3.5" /></a>
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                        <a href={`https://wa.me/${client.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                          <MessageSquare className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                      {client.email && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                          <a href={`mailto:${client.email}`}><Mail className="h-3.5 w-3.5" /></a>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
